import intelliJetNetService, { QueryBuilder } from '../intellijetnet'
import { Aircraft } from '../../types/intellijetnet'
import { ensureArray, executeQuery, executeQueryFirst } from './utils'

export class AircraftService {
  private static readonly TABLE_NAME = 'aircraft'

  /**
   * Get all aircraft with optional filters
   * Note: For market coverage filtering, use getByModelIds after fetching allowed model IDs
   */
  static query(): QueryBuilder {
    return intelliJetNetService.from(this.TABLE_NAME)
  }

  /**
   * Get aircraft by model IDs (useful for market coverage filtering)
   */
  static async getByModelIds(modelIds: number[]): Promise<Aircraft[]> {
    if (modelIds.length === 0) return []
    return executeQuery<Aircraft>(
      this.query()
        .in('model_id', modelIds)
        .eq('is_active', true)
    )
  }

  /**
   * Get aircraft by ID
   */
  static async getById(aircraftId: number): Promise<Aircraft | null> {
    return executeQueryFirst<Aircraft>(
      this.query().eq('aircraft_id', aircraftId)
    )
  }

  /**
   * Get aircraft by serial number
   */
  static async getBySerialNumber(serialNumber: string): Promise<Aircraft | null> {
    return executeQueryFirst<Aircraft>(
      this.query().eq('sernbr', serialNumber)
    )
  }

  /**
   * Get aircraft by registration number
   */
  static async getByRegistration(regNumber: string): Promise<Aircraft | null> {
    return executeQueryFirst<Aircraft>(
      this.query().eq('regnbr', regNumber)
    )
  }

  /**
   * Get aircraft for sale
   */
  static async getForSale(limit = 100): Promise<Aircraft[]> {
    return executeQuery<Aircraft>(
      this.query()
        .eq('forsale', true)
        .eq('is_active', true)
        .order('updated_at', false)
        .limit(limit)
    )
  }

  /**
   * Get aircraft by make and model
   */
  static async getByMakeModel(make: string, model?: string): Promise<Aircraft[]> {
    let query = this.query().eq('make', make)
    if (model) query = query.eq('model', model)
    
    return executeQuery<Aircraft>(
      query
        .eq('is_active', true)
        .order('yearmfr', false)
    )
  }

  /**
   * Get aircraft by model ID
   */
  static async getByModelId(modelId: number): Promise<Aircraft[]> {
    return executeQuery<Aircraft>(
      this.query()
        .eq('model_id', modelId)
        .eq('is_active', true)
        .order('yearmfr', false)
    )
  }

  /**
   * Get aircraft by weight class
   */
  static async getByWeightClass(weightClass: string): Promise<Aircraft[]> {
    return executeQuery<Aircraft>(
      this.query()
        .eq('weightclass', weightClass)
        .eq('is_active', true)
    )
  }

  /**
   * Get aircraft by base location
   */
  static async getByBaseLocation(country?: string, state?: string, city?: string): Promise<Aircraft[]> {
    let query = this.query()
    if (country) query = query.eq('basecountry', country)
    if (state) query = query.eq('basestate', state)
    if (city) query = query.eq('basecity', city)
    
    return executeQuery<Aircraft>(
      query.eq('is_active', true)
    )
  }

  /**
   * Search aircraft by multiple criteria
   */
  static async search(criteria: {
    make?: string
    model?: string
    yearFrom?: number
    yearTo?: number
    forSale?: boolean
    leased?: boolean
    weightClass?: string
    baseCountry?: string
    maxPrice?: string
  }): Promise<Aircraft[]> {
    let query = this.query().eq('is_active', true)

    if (criteria.make) query = query.ilike('make', `%${criteria.make}%`)
    if (criteria.model) query = query.ilike('model', `%${criteria.model}%`)
    if (criteria.yearFrom) query = query.gte('yearmfr', criteria.yearFrom)
    if (criteria.yearTo) query = query.lte('yearmfr', criteria.yearTo)
    if (criteria.forSale !== undefined) query = query.eq('forsale', criteria.forSale)
    if (criteria.leased !== undefined) query = query.eq('leased', criteria.leased)
    if (criteria.weightClass) query = query.eq('weightclass', criteria.weightClass)
    if (criteria.baseCountry) query = query.eq('basecountry', criteria.baseCountry)
    if (criteria.maxPrice) query = query.lte('asking', criteria.maxPrice)

    return executeQuery<Aircraft>(
      query
        .order('updated_at', false)
        .limit(200)
    )
  }

  /**
   * Get aircraft with relationships (model info)
   */
  static async getWithModel(aircraftId: number): Promise<Aircraft & { model_info?: any } | null> {
    return executeQueryFirst<Aircraft & { model_info?: any }>(
      this.query()
        .select('*, aircraft_models(*)')
        .eq('aircraft_id', aircraftId)
    )
  }

  /**
   * Get recently updated aircraft
   */
  static async getRecentlyUpdated(days = 7, limit = 50): Promise<Aircraft[]> {
    const date = new Date()
    date.setDate(date.getDate() - days)
    
    return executeQuery<Aircraft>(
      this.query()
        .gte('updated_at', date.toISOString())
        .eq('is_active', true)
        .order('updated_at', false)
        .limit(limit)
    )
  }

  /**
   * Get aircraft statistics
   */
  static async getStatistics(): Promise<{
    total: number
    forSale: number
    leased: number
    byWeightClass: Record<string, number>
  }> {
    // Get total count
    const { count: total } = await this.query()
      .eq('is_active', true)
      .count('exact')
      .execute<Aircraft>()

    // Get for sale count
    const { count: forSale } = await this.query()
      .eq('is_active', true)
      .eq('forsale', true)
      .count('exact')
      .execute<Aircraft>()

    // Get leased count
    const { count: leased } = await this.query()
      .eq('is_active', true)
      .eq('leased', true)
      .count('exact')
      .execute<Aircraft>()

    // Get counts by weight class
    const result = await intelliJetNetService
      .rpc('get_aircraft_by_weight_class_count')
    const weightClassData = ensureArray<{ weightclass: string; count: number }>(result)

    const byWeightClass = weightClassData.reduce((acc: Record<string, number>, item) => {
      acc[item.weightclass] = item.count
      return acc
    }, {})

    return {
      total: total || 0,
      forSale: forSale || 0,
      leased: leased || 0,
      byWeightClass
    }
  }
}

export default AircraftService